"use client";
import { hamtaSessionSvar } from "@/lib/klient-session";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";
import { MATE_KURS_TO_MA, type MaId, type ResolverResult } from "@/lib/formelblad-shared";

const MA_TO_PDF: Record<MaId, string> = {
  ma1: "/formelblad/ma1.pdf",
  ma2: "/formelblad/ma2.pdf",
  ma3: "/formelblad/ma3.pdf",
  ma4: "/formelblad/ma4.pdf",
  ma5: "/formelblad/ma5.pdf",
};

const STRIP_LINKS: Array<{ id: MaId; kursKod: string; label: string }> = [
  { id: "ma1", kursKod: "MATE1A00X", label: "ma1" },
  { id: "ma2", kursKod: "MATE2A00X", label: "ma2" },
  { id: "ma3", kursKod: "MATO1C00X", label: "ma1 forts." },
  { id: "ma4", kursKod: "MATO2000X", label: "ma2 forts." },
  { id: "ma5", kursKod: "MATF1000X", label: "ma fördj." },
];

const STORAGE_KEY = "vektr.formelbladChoice";

type LocalChoice = { kursKod: string; ts: number };

function readLocalChoice(): LocalChoice | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LocalChoice;
    if (!parsed || typeof parsed.kursKod !== "string" || typeof parsed.ts !== "number") {
      return null;
    }
    if (!MATE_KURS_TO_MA[parsed.kursKod]) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLocalChoice(kursKod: string) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ kursKod, ts: Date.now() }));
  } catch {
    // localStorage disabled (private mode) — synthetic activity går förlorat
    // men funktionen fungerar fortfarande för stunden.
  }
}

function detectMateKursFromPathname(pathname: string): string | null {
  const match = pathname.match(/\/kurser\/([^/]+)/);
  if (!match) return null;
  const kod = match[1].toUpperCase();
  return MATE_KURS_TO_MA[kod] ? kod : null;
}

interface SessionUser {
  role: string;
  impersonating?: { studentId: string };
}

function PaperLinesIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 3h9l4 4v14H6V3z" />
      <path d="M15 3v4h4" />
      <line x1="9" y1="11" x2="16" y2="11" />
      <line x1="9" y1="14" x2="16" y2="14" />
      <line x1="9" y1="17" x2="14" y2="17" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
    >
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="6" y1="18" x2="18" y2="6" />
    </svg>
  );
}

export default function FormelbladButton() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState<SessionUser | null>(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  // resolvedKursKod = server + localStorage-resultatet. Sätts asynkront vid
  // modal-öppning. clickedKursKod = användarens senaste strip-klick. URL-träff
  // evalueras synkront vid render. Prioritet i render: click > url > resolved
  // > fallback.
  const [resolvedKursKod, setResolvedKursKod] = useState<string | null>(null);
  const [clickedKursKod, setClickedKursKod] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    hamtaSessionSvar<{ user?: SessionUser }>()
      .then((d) => {
        if (cancelled) return;
        setUser(d?.user ?? null);
        setSessionLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setSessionLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Visas för varje inloggad användare som får se studie-verktygen. Lärare ser
  // exakt samma formelblad som eleverna — de behöver kunna slå upp bladet på
  // samma villkor som klassen. Admin/skol-admin når aldrig hit: Nav renderar
  // hela studie-verktygs-gruppen bara för elev- och lärar-roller.
  const visa = sessionLoaded && user !== null;

  // Esc stänger modalen — måste ligga före conditional return så hooks-ordningen
  // är stabil mellan renders.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    document.body.dataset.modalOpen = "true";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      delete document.body.dataset.modalOpen;
    };
  }, [open]);

  // Server + localStorage-resolution: körs när modalen öppnas och inte är på
  // en /kurser/<kod>-rutt. URL-träffen evalueras synkront vid render nedan.
  useEffect(() => {
    if (!open) return;
    if (detectMateKursFromPathname(pathname || "")) return; // URL vinner — skippa fetch
    let cancelled = false;

    fetch("/api/formelblad/resolve", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((res: ResolverResult | null) => {
        if (cancelled) return;
        if (!res) {
          setResolvedKursKod("MATE1A00X");
          return;
        }

        // Steg 2: server-bekräftad single-registered → använd alltid den
        if (res.source === "single" && res.kursKod) {
          setResolvedKursKod(res.kursKod);
          return;
        }

        // Steg 3: merga localStorage-valet med server-aktiviteten, plocka högsta ts
        const merged: Record<string, number> = { ...res.serverTs };
        const localChoice = readLocalChoice();
        if (localChoice) {
          merged[localChoice.kursKod] = Math.max(
            merged[localChoice.kursKod] ?? 0,
            localChoice.ts,
          );
        }

        let winner: string | null = null;
        let winnerTs = 0;
        for (const [kod, ts] of Object.entries(merged)) {
          if (ts > winnerTs && MATE_KURS_TO_MA[kod]) {
            winner = kod;
            winnerTs = ts;
          }
        }

        // Steg 4: fallback
        setResolvedKursKod(winner ?? "MATE1A00X");
      })
      .catch(() => {
        if (!cancelled) setResolvedKursKod("MATE1A00X");
      });

    return () => {
      cancelled = true;
    };
  }, [open, pathname]);

  if (!visa) return null;

  const urlKursKod = detectMateKursFromPathname(pathname || "");
  const aktivKursKod =
    clickedKursKod ?? urlKursKod ?? resolvedKursKod ?? "MATE1A00X";
  const aktivMa = MATE_KURS_TO_MA[aktivKursKod] ?? "ma1";
  const pdfUrl = MA_TO_PDF[aktivMa];

  function handleOpen() {
    // Reset click-overriden så nya öppningen startar från resolution-kedjan.
    setClickedKursKod(null);
    setResolvedKursKod(null);
    setOpen(true);
  }

  function handleStripClick(kursKod: string) {
    setClickedKursKod(kursKod);
    writeLocalChoice(kursKod);
  }

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        aria-label="Formelblad"
        title="Öppna formelblad — växla blad direkt med ma1–ma5-länkarna i toppen"
        className="-ml-2 p-2 rounded-md text-[var(--color-nav-text)] hover:bg-[var(--color-nav-border)] transition-colors"
      >
        <PaperLinesIcon className="w-5 h-5" />
      </button>

      {open && typeof document !== "undefined" && createPortal(
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Formelblad"
        >
          <div
            className="relative rounded-lg w-[95vw] h-[92vh] max-w-5xl flex flex-col shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{ background: "var(--color-bg)" }}
          >
            <div
              className="flex items-end justify-between px-3 pt-2"
              style={{
                background: "var(--color-nav-bg)",
                borderBottom: "1px solid color-mix(in oklab, var(--color-nav-bg) 70%, black)",
              }}
            >
              <div className="flex items-end gap-1 text-xs">
                {STRIP_LINKS.map((l) => {
                  const active = aktivMa === l.id;
                  return (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => handleStripClick(l.kursKod)}
                      title={`Visa formelblad: ${l.label}`}
                      className="px-2.5 py-1 transition-colors"
                      style={{
                        border: "1px solid color-mix(in oklab, var(--color-nav-bg) 65%, black)",
                        borderBottom: active
                          ? "1px solid var(--color-bg)"
                          : "1px solid color-mix(in oklab, var(--color-nav-bg) 65%, black)",
                        borderTopLeftRadius: "0.25rem",
                        borderTopRightRadius: "0.25rem",
                        color: active ? "var(--color-text)" : "var(--color-text-muted)",
                        background: active ? "var(--color-bg)" : "var(--color-sidebar-bg)",
                        marginBottom: "-1px",
                      }}
                    >
                      {l.label}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Stäng"
                title="Stäng formelbladet"
                className="p-1.5 rounded-full transition-colors"
                style={{ color: "var(--color-text)" }}
              >
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>
            <iframe
              key={pdfUrl}
              src={`${pdfUrl}#view=FitH&toolbar=0&navpanes=0`}
              title="Formelblad"
              className="flex-1 w-full"
              style={{ background: "var(--color-bg)" }}
            />
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
